/**
 * ClarificationPrompt Component
 *
 * Displays clarification options when query understanding needs user input.
 * Shows a message explaining why clarification is needed and clickable options.
 * Uses a searchable combobox for large option lists (filter_required, low_confidence).
 * Responsive: Popover on desktop, Drawer on mobile for better touch UX.
 */

import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import type { ClarificationOption, ClarificationResponse } from '../../types';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  HelpCircle,
  Search,
  AlertTriangle,
  Filter,
  Loader2,
  ChevronsUpDown,
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ClarificationPromptProps {
  clarification: ClarificationResponse;
  onSelect: (option: ClarificationOption) => void;
  onDismiss: () => void;
  isLoading?: boolean;
}

// Threshold for using combobox vs button list
const COMBOBOX_THRESHOLD = 4;

export const ClarificationPrompt = ({
  clarification,
  onSelect,
  onDismiss,
  isLoading = false,
}: ClarificationPromptProps) => {
  const { message, options, clarification_type } = clarification;
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const isMobile = useIsMobile();

  // Sort options alphabetically for better UX
  const sortedOptions = useMemo(
    () =>
      [...options].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [options],
  );

  // Use combobox for filter_required/low_confidence with many options
  const useCombobox =
    (clarification_type === 'filter_required' ||
      clarification_type === 'low_confidence') &&
    options.length >= COMBOBOX_THRESHOLD;

  // Get icon based on clarification type
  const getIcon = () => {
    switch (clarification_type) {
      case 'ambiguous':
        return <HelpCircle className="h-5 w-5" />;
      case 'no_match':
        return <Search className="h-5 w-5" />;
      case 'low_confidence':
        return <AlertTriangle className="h-5 w-5" />;
      case 'filter_required':
        return <Filter className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (clarification_type) {
      case 'ambiguous':
        return 'Multiple matches found';
      case 'no_match':
        return 'No matching entity found';
      case 'low_confidence':
        return 'Please clarify';
      case 'filter_required':
        return 'Select a scope';
      default:
        return 'Clarification needed';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-2xl mx-auto"
      role="dialog"
      aria-labelledby="clarification-title"
      aria-describedby="clarification-message"
    >
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-800/30 rounded-full text-amber-600 dark:text-amber-400">
              {getIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle
                id="clarification-title"
                className="text-amber-800 dark:text-amber-200 text-sm"
              >
                {getTitle()}
              </CardTitle>
              <CardDescription
                id="clarification-message"
                className="text-amber-700 dark:text-amber-300 mt-1"
              >
                {message}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Options - combobox for large lists, buttons for small lists */}
          {options.length > 0 ? (
            useCombobox ? (
              /* Responsive combobox: Drawer on mobile, Popover on desktop */
              <div className="mb-4">
                {isMobile ? (
                  /* Mobile: Bottom drawer for better touch UX */
                  <Drawer open={comboboxOpen} onOpenChange={setComboboxOpen}>
                    <DrawerTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={comboboxOpen}
                        aria-label="Select a client"
                        disabled={isLoading}
                        className="w-full justify-between hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10"
                      >
                        {isLoading ? (
                          <>
                            <Loader2
                              className="h-4 w-4 mr-2 animate-spin"
                              aria-hidden="true"
                            />
                            Searching...
                          </>
                        ) : (
                          'Select a client...'
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent>
                      <DrawerTitle className="sr-only">
                        Select a client
                      </DrawerTitle>
                      <DrawerDescription className="sr-only">
                        Search and select a client to filter your query
                      </DrawerDescription>
                      <div className="mt-4 border-t">
                        <Command>
                          <CommandInput placeholder="Search clients..." />
                          <CommandList className="max-h-[50vh]">
                            <CommandEmpty>No client found.</CommandEmpty>
                            <CommandGroup>
                              {sortedOptions.map((option) => (
                                <CommandItem
                                  key={option.id}
                                  value={option.display_name}
                                  onSelect={() => {
                                    setComboboxOpen(false);
                                    onSelect(option);
                                  }}
                                  className="py-3"
                                >
                                  {option.display_name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </div>
                    </DrawerContent>
                  </Drawer>
                ) : (
                  /* Desktop: Popover dropdown */
                  <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={comboboxOpen}
                        aria-label="Select a client"
                        disabled={isLoading}
                        className="w-full justify-between hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10"
                      >
                        {isLoading ? (
                          <>
                            <Loader2
                              className="h-4 w-4 mr-2 animate-spin"
                              aria-hidden="true"
                            />
                            Searching...
                          </>
                        ) : (
                          'Select a client...'
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="p-0"
                      style={{ width: 'var(--radix-popover-trigger-width)' }}
                      align="start"
                    >
                      <Command>
                        <CommandInput placeholder="Search clients..." />
                        <CommandList>
                          <CommandEmpty>No client found.</CommandEmpty>
                          <CommandGroup>
                            {sortedOptions.map((option) => (
                              <CommandItem
                                key={option.id}
                                value={option.display_name}
                                onSelect={() => {
                                  setComboboxOpen(false);
                                  onSelect(option);
                                }}
                              >
                                {option.display_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            ) : (
              /* Button list for small option lists */
              <div
                className="space-y-2 mb-4"
                role="group"
                aria-label="Clarification options"
              >
                {sortedOptions.map((option, index) => (
                  <motion.div
                    key={option.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Button
                      variant="outline"
                      onClick={() => onSelect(option)}
                      disabled={isLoading}
                      className="w-full justify-start text-left h-auto py-3 hover:border-amber-400 dark:hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/10"
                    >
                      {isLoading && (
                        <Loader2
                          className="h-4 w-4 mr-2 animate-spin"
                          aria-hidden="true"
                        />
                      )}
                      {option.display_name}
                    </Button>
                  </motion.div>
                ))}
              </div>
            )
          ) : (
            /* Empty options - provide guidance */
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
              Try rephrasing your question to include the client name, for
              example: "What are Acme Corp's sales this month?"
            </p>
          )}

          {/* Dismiss button - allows user to rephrase their query */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={onDismiss}
              disabled={isLoading}
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
